import { useFlags } from '@nx/launch-darkly-service';
import type { FleetManagementTypes, Instance, MetricsResult, TimeRange } from '@nx/state';
import { MODAL_TYPE, useActiveProject, useModal } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { Grid } from '@nx/ui';
import { useMemo } from 'react';

import { MAX_DATABASES_FOR_METRICS } from '../fleet-management/shared/constants';
import { FakeMetricsOverlay, NoInstancesOverlay } from '../shared/components';
import { fakeMetrics } from '../shared/utils';
import { AuraDatabaseCharts } from './charts/database-charts';
import type { ChartType } from './charts/fullstack-chart-props';
import { AuraInstanceCharts } from './charts/instance-charts';
import { LineCard, OptionalFeatures } from './charts/line-cards';
import { AuraSystemCharts } from './charts/system-charts';
import type { ChartContext, ChartExpandProps, MultiChartProps } from './charts/types';
import type { MetricsViewState } from './hooks';
import { chartKey, useMetricsMetadata, useMetricsQuery } from './hooks';
import type { MetricSource } from './shared/types';

const ViewCharts: Record<ChartType, MultiChartProps[]> = {
  HOST: AuraSystemCharts,
  INSTANCE: AuraInstanceCharts,
  DB: AuraDatabaseCharts,
  DASHBOARD: [],
};

const TIME_RANGE_PADDED = true;
const MIN_PADDING_MS = 5 * 60 * 1000;
const PADDING_PERCENTAGE = 0.1;

const getPadding = ({ startTime, endTime }: TimeRange) => {
  const diff = endTime.getTime() - startTime.getTime();
  return Math.max(MIN_PADDING_MS, diff * PADDING_PERCENTAGE);
};
export const padTimeRange = (timeRange: TimeRange): TimeRange => {
  const paddingMs = getPadding(timeRange);
  const startTime = new Date(timeRange.startTime.getTime() - paddingMs);
  const endTime = new Date(timeRange.endTime.getTime() + paddingMs);
  return {
    startTime,
    endTime,
  };
};

type MetricsViewProps = {
  metricSource?: MetricSource;
  viewType: ChartType;
  viewState: MetricsViewState;
  chartContext: ChartContext;
  expandProps: ChartExpandProps;
  onZoom?: (timeRange: TimeRange) => void | undefined;
  onZoomReset?: () => void | undefined;
};

const MetricsView = ({
  metricSource,
  viewType,
  chartContext,
  viewState,
  expandProps,
  onZoom,
  onZoomReset,
}: MetricsViewProps) => {
  const activeProject = useActiveProject();
  const { timeRangeZoomedIn, expandedChart } = viewState;
  const timeRange = timeRangeZoomedIn ? padTimeRange(chartContext.timeRange) : chartContext.timeRange;

  const flags = useFlags();
  const viewChartProps = useMemo(() => metricSource?.getCharts(flags)[viewType], [metricSource, flags, viewType]);

  const { metricsMetadata, isError: isMetricsMetadataErr } = useMetricsMetadata(
    isNotNullish(metricSource) ? activeProject.id : undefined,
  );

  const shallowChartProps = useMemo(() => {
    return (
      viewChartProps?.map((value) => {
        // Only request the first metric of each multi-chart
        return Array.isArray(value) && value.length > 1 ? value[0]! : value;
      }) ?? []
    );
  }, [viewChartProps]);

  const metricsResult = useMetricsQuery({
    metricSource,
    timeRange,
    chartProps: shallowChartProps,
    chartType: viewType,
  });

  return (
    <Grid>
      {viewChartProps?.map((chart) => (
        <LineCard
          key={chartKey(chart)}
          multiChartProps={chart}
          context={chartContext}
          metricsResult={metricsResult}
          expandProps={{ ...expandProps, isExpanded: expandedChart === chart }}
          onZoom={onZoom}
          onZoomReset={onZoomReset}
          timeRangePadded={TIME_RANGE_PADDED}
          addMinMaxBands={true}
          optionalFeatures={OptionalFeatures.AuraWithLogs}
          metadata={!isMetricsMetadataErr ? metricsMetadata : undefined}
          timeRange={timeRange}
          metricSource={metricSource}
        />
      ))}
    </Grid>
  );
};

const FakeMetricsView = ({
  instanceName,
  viewType,
  chartContext,
}: {
  instanceName: string;
  viewType: ChartType;
  chartContext: ChartContext;
}) => {
  const chartProps = ViewCharts[viewType];

  const fakeMetricsResult: MetricsResult = useMemo(
    () => ({
      metrics: fakeMetrics(instanceName, 'LIVE', chartProps),
      isLoading: false,
    }),
    [chartProps, instanceName],
  );

  return (
    <Grid>
      {chartProps.map((chart) => (
        <LineCard
          key={chartKey(chart)}
          multiChartProps={chart}
          context={chartContext}
          metricsResult={fakeMetricsResult}
          expandProps={{ isExpanded: false }}
          timeRangePadded={TIME_RANGE_PADDED}
          addMinMaxBands={true}
          optionalFeatures={OptionalFeatures.Aura}
          timeRange={{ startTime: new Date(), endTime: new Date() }}
        />
      ))}
    </Grid>
  );
};

type DeploymentViewProps = {
  deployment: FleetManagementTypes.DeploymentDetails;
  viewType: ChartType;
  chartContext: ChartContext;
};

export const UpgradeDeploymentView = ({ deployment, viewType, chartContext }: DeploymentViewProps) => {
  const createInstanceModal = useModal(MODAL_TYPE.INSTANCE_CREATE);
  return (
    <>
      <FakeMetricsOverlay
        title="Upgrade for metrics"
        body="An AuraDB Professional or Business Critical instance will provide access to metrics."
        onUpgrade={() => createInstanceModal.open({ isOnboarding: false, preselectTier: undefined })}
        upgradeButtonText="Create instance"
        docsLink="https://neo4j.com/docs/aura/tutorials/migration/"
      >
        <FakeMetricsView instanceName={deployment.name} viewType={viewType} chartContext={chartContext} />
      </FakeMetricsOverlay>
    </>
  );
};

export const MetricsNotSupportedView = ({ deployment, viewType, chartContext }: DeploymentViewProps) => {
  return (
    <>
      <FakeMetricsOverlay
        title="Metric collection is disabled"
        body={`Metric collection is disabled for deployments with more than ${MAX_DATABASES_FOR_METRICS} databases.`}
      >
        <FakeMetricsView instanceName={deployment.name} viewType={viewType} chartContext={chartContext} />
      </FakeMetricsOverlay>
    </>
  );
};

type UpgradeMetricsViewProps = {
  instance?: Instance;
  viewType: ChartType;
  chartContext: ChartContext;
};

export const UpgradeMetricsView = ({ instance, viewType, chartContext }: UpgradeMetricsViewProps) => {
  const activeProject = useActiveProject();
  const upgradeModal = useModal(MODAL_TYPE.UPGRADE);

  return (
    <>
      <FakeMetricsOverlay
        title="Upgrade to Professional for metrics"
        body="Upgrading to professional enables access to a broad range of different instance and database metrics."
        onUpgrade={() => upgradeModal.open({ project: activeProject, instance: instance!, targetTier: undefined })}
      >
        <FakeMetricsView instanceName={instance?.name ?? ''} viewType={viewType} chartContext={chartContext} />
      </FakeMetricsOverlay>
    </>
  );
};

export const NoInstancesView = ({ viewType, chartContext }: { viewType: ChartType; chartContext: ChartContext }) => {
  return (
    <NoInstancesOverlay
      title="No instances found"
      body="Create an instance to enable access to a broad range of different instance and database metrics."
    >
      <div className="m-4 flex flex-col gap-4">
        <FakeMetricsView instanceName="fake" viewType={viewType} chartContext={chartContext} />
      </div>
    </NoInstancesOverlay>
  );
};

export default MetricsView;
